import React from 'react';
import styled from 'styled-components';
import { Icon } from '../';
import Text from '../Text/Text';
import { colors, typoGraphy } from '../../../stylesheets';

export const StyledErrorText = styled(Text)`
  position: absolute;
  bottom: -25px;
`;

function InputElement(props: Props) {
  return React.createElement(props.element || 'input', { ...props });
}

const StyledInput = styled(InputElement)((props) => ({
  direction: 'rtl',
  ...(props.disabled ? { cursor: 'not-allowed' } : {}),
  color: colors['blackText'],
  width: '100%',
  borderRadius: '10px',
  border: '1px solid ' + colors['lightGray'],
  backgroundColor: getErrorBackgroundColor(props),
  padding: props.placeholderIconName ? '8px 35px' : '8px 18px',
  ...typoGraphy['sm'],
  '::placeholder': {
    color: getPlaceHolderColor(props),
    ...typoGraphy['sm'],
  },
  WebkitAppearance: 'none',
  ...(props.type === 'number'
    ? { MozAppearance: 'textfield', WebkitAppearance: 'none' }
    : {}),
  ...(props.addon
    ? { borderLeft: '0', borderTopLeftRadius: '0', borderBottomLeftRadius: 0 }
    : {}),
}));

const IconWrapper = styled.div`
  position: absolute;
  left: 15px;
  transform: translate(-50%, -50%);
  top: 55%;
`;

const PlaceholderIconWrapper = styled.div`
  position: absolute;
  right: 0;
  transform: translate(-50%, -50%);
  top: 55%;
`;

const StyledWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

type Props = {
  placeholderIconName?: string,
  placeholder: string,
  value: string,
  onChange: Function,
  disabled?: boolean,
  iconName?: string,
  iconColor?: string,
  onIconClick?: Function,
  error: string,
  inputClassName?: String,
  className?: String,
  label?: String,
  onBlur?: Function,
  labelClassName?: String,
};

const getErrorBackgroundColor = ({ error, disabled }) => {
  if (error) {
    return colors['lighterPrimary'];
  }
  if (disabled) {
    return colors['brightGray'];
  }
  return colors['white'];
};

const getPlaceHolderColor = ({ error, disabled }) => {
  if (error) {
    return colors['primaryText'];
  }
  if (disabled) {
    return colors['grayText'];
  }
  return colors['grayText'];
};

const getIconColor = ({ error, disabled }) => {
  if (error) {
    return 'primaryText';
  }
  if (disabled) {
    return 'lightGray';
  }
  return 'gray';
};

TextInput.defaultProps = {
  type: 'text',
  className: '',
  inputClassName: '',
  element: 'input',
};

const Addon = styled.span((props) => ({
  padding: '0 1rem',
  border: `1px solid ${colors.lightGray}`,
  borderRight: 'none',
  height: '36px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '15px 0 0 15px',
  backgroundColor: colors.lighterGray,
}));

export default function TextInput(props: Props) {
  const {
    className,
    inputClassName,
    name,
    type,
    disabled,
    value,
    placeholder,
    onChange,
    iconName,
    onIconClick,
    placeholderIconName,
    error,
    label,
    labelSize="md",
    is_bold=false,
    onBlur,
    labelClassName,
    addon,
    onClick = () => console.log,
    ref=null,
    iconStyles={},
    ...rest
  } = props;
  const Input = (
    <StyledInput
      ref={ref}
      className={inputClassName}
      name={name}
      type={type}
      disabled={disabled}
      value={value}
      placeholder={placeholder}
      onChange={(e) =>
        onChange(e, e.target[type === 'file' ? 'file' : 'value'])
      }
      onClick={onClick}
      onBlur={onBlur}
      addon={addon}
      {...rest}
    />
  );
  return (
    <StyledWrapper className={`my-1 ${className}`}>
      {label ? (
        <>
          <Text
            type="label"
            htmlFor={name}
            size={labelSize}
            bold={is_bold}
            className={`mb-1 w-100 ${labelClassName} ${
              error ? 'has-error' : ''
            }`}
          >
            {label}
          </Text>
          {/* {error ? (
            <span className="px-1 font-xs text-red">- {error}</span>
          ) : null} */}
        </>
      ) : null}
      {addon ? (
        <div className="flex-center">
          {Input}
          <Addon>{addon}</Addon>
        </div>
      ) : (
        Input
      )}
      {iconName ? (
        <IconWrapper style={iconStyles} onClick={onIconClick}>
          <Icon name={iconName} color={disabled ? 'lightGray' : 'blackText'} />
        </IconWrapper>
      ) : null}
      {placeholderIconName ? (
        <PlaceholderIconWrapper>
          <Icon name={placeholderIconName} color={getIconColor(props)} />
        </PlaceholderIconWrapper>
      ) : null}
      {error ? (
        <StyledErrorText size="md" color="red" className="mr-2" dir="rtl">
          * {error}
        </StyledErrorText>
      ) : null}
    </StyledWrapper>
  );
}
